import {
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  PLATFORM_ID,
  TransferState,
  ViewChild,
  computed,
  inject,
  makeStateKey,
  signal,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, map, of } from 'rxjs';
import { RouterLink } from '@angular/router';
import { BigSaleBanner } from '../../../shared/big-sale-banner/big-sale-banner';
import { ProductCard } from '../../../shared/product-card/product-card';
import { DevFlag } from '../../../shared/dev-flag/dev-flag';
import { SAVING_ARENA_API_CONFIG } from '../../../core/api/api.config';
import { LATEST_INSIGHTS_DISPLAY_LIMIT } from '../../../shared/latest-insights/latest-insights';
import {
  FrontendBlog,
  FrontendDealCategoryListItem,
  FrontendDealsBannerSlide,
  FrontendDealsCategoryProducts,
  FrontendFeaturedEventStore,
  FrontendPopularCategory,
  FrontendProduct,
} from '../../../core/api/api.models';
import { blogBodyText, blogReadMinutes } from '../../../core/api/blog-text';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { SeoService } from '../../../core/seo/seo.service';
import { formatCompactCount } from '../../../core/format/compact-count';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { StoreLogo } from '../../../shared/store-logo/store-logo';

const DEALS_PATH = '/collection';
// SSR fetches with an internal URL and the browser with the public one, so Angular's HTTP transfer
// cache can't match them. Transferring the mapped slides under our own key keeps the hero stable
// through hydration instead of blanking and refetching.
const DEALS_HERO_KEY = makeStateKey<HeroSlide[]>('deals-hero-slides');
// Same reasoning as the hero key: the SSR and browser API base URLs differ, so Angular's HTTP
// transfer cache never matches and the arrivals would blank on hydration before refetching.
const DEALS_NEW_ARRIVALS_KEY = makeStateKey<DealProduct[]>('deals-new-arrivals');
const DEALS_TOP_PICKS_KEY = makeStateKey<TopPicksRow>('deals-top-picks');
const DEALS_TOP_STORES_KEY = makeStateKey<EventTopStore[]>('deals-top-stores');
const DEALS_EVENT_STORES_KEY = makeStateKey<DealBrandCard[]>('deals-event-stores');
const DEALS_CATEGORY_PRODUCTS_KEY =
  makeStateKey<CategoryProductsSection[]>('deals-category-products');
const DEALS_BLOG_POSTS_KEY = makeStateKey<DealBlogPost[]>('deals-blog-posts');
// The home page's guides row shows the newest posts; this page picks up right after them, so the
// same article never appears on both.
const BLOG_SKIP = LATEST_INSIGHTS_DISPLAY_LIMIT;
// Six fills whole rows of this section's 1 / 2 / 3 column grid.
const BLOG_DISPLAY_LIMIT = 6;
// Both slices plus a buffer, so posts dropped in mapping (no title or slug) still leave six.
const BLOG_FETCH_LIMIT = BLOG_SKIP + BLOG_DISPLAY_LIMIT + 6;
// One full row at every `.sav-product-grid` breakpoint (2 / 3 / 4 / 6 columns).
const NEW_ARRIVALS_LIMIT = 12;
const TOP_PICKS_LIMIT = 12;
const TOP_STORES_LIMIT = 14;
const EVENT_STORES_LIMIT = 12;
const DEALS_TITLE = 'Deals & Product Offers | SavingArena';
const DEALS_DESCRIPTION =
  'Browse the latest deals, product drops and brand offers on SavingArena — curated discounts across smart home, car tech and everyday essentials.';

type PopularCategoryThumb = 'men' | 'women' | 'accessories' | 'outerwear' | 'shoes';

interface HeroSlide {
  imageUrl: string;
  altText: string;
  linkUrl: string;
  isExternal: boolean;
}

interface DealProduct {
  title: string;
  price: string;
  oldPrice: string;
  save: string;
  reviews: number;
  image: string;
  hoverImage: string;
  storeName: string;
  storeLogoUrl: string;
  href: string;
}

interface DealBlogPost {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  author: string;
  authorPath: string;
  imageUrl: string;
  storeLogoUrl: string;
}

interface DealBrandCard {
  name: string;
  logoUrl: string;
  couponsCount: number;
  href: string;
}

interface EventTopStore {
  name: string;
  logoUrl: string;
  path: string;
}

interface FeaturedHero {
  name: string;
  /** Store coupon page slug — where the Follow signup form lives. */
  slug: string;
  path: string;
  logoUrl: string;
  bannerUrl: string;
  affiliateLink: string;
  buttonText: string;
  followers: number;
  shares: number;
  /** Flips to a confirmation after a share, then back. */
  shareLabel: string;
}

/** "Shop Our Top Picks" as the API resolved it — admin-chosen stores, or the automatic ranking. */
interface TopPicksRow {
  sectionTitle: string;
  products: DealProduct[];
}

/** One admin-chosen category row. The list is empty when the setting is unset. */
interface CategoryProductsSection {
  categoryName: string;
  categoryPath: string;
  products: DealProduct[];
}

interface PopularCategoryItem {
  label: string;
  /** Legacy stock thumbnail keyed by name; only used when the category has no real image. */
  thumb?: PopularCategoryThumb;
  slug?: string;
  /** Category page this tile links to. */
  path?: string;
  /** The admin's category icon, or the category's newest product image. */
  imageUrl?: string;
  /** Inline SVG the admin stored instead of an image. */
  iconHtml?: SafeHtml | null;
}

interface PopularCategoryTab {
  id: string;
  label: string;
  categories: PopularCategoryItem[];
}

@Component({
  selector: 'app-deals-page',
  imports: [Breadcrumb, RouterLink, ProductCard, DevFlag, BigSaleBanner, StoreLogo],
  templateUrl: './deals-page.html',
  styleUrl: './deals-page.scss',
})
export class DealsPage implements OnDestroy {
  // "Deals" is what the schema has always called this page; the URL is /collection.
  protected readonly crumbs = breadcrumbTrail({ label: 'Deals' });

  private readonly seo = inject(SeoService);
  private readonly api = inject(SavingArenaApiService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transferState = inject(TransferState);
  private readonly isServer = isPlatformServer(inject(PLATFORM_ID));
  @ViewChild('topStoresTrack') private readonly topStoresTrack?: ElementRef<HTMLDivElement>;
  @ViewChild('featuredHeroTrack') private readonly featuredHeroTrack?: ElementRef<HTMLDivElement>;
  private heroAutoplayTimer: ReturnType<typeof setInterval> | null = null;
  private popularCategoryAutoplayTimer: ReturnType<typeof setInterval> | null = null;

  // ── Hero carousel — slides come from the admin (/settings/deals-page/banner) ───
  // No fallback on purpose: with no banners configured the section renders nothing at all.
  protected readonly heroSlides = signal<HeroSlide[]>([]);
  // Live data: the newest product from each store, so the row shows real new arrivals across the
  // catalogue rather than a dozen size variants of one item.
  protected readonly newArrivals = signal<DealProduct[]>([]);
  // Each store's deepest markdown, biggest discount first. One per store because a feed lists every
  // size of a garment separately, which would otherwise fill the row with the same item.
  protected readonly topPicks = signal<DealProduct[]>([]);
  // Admin heading for that row; blank means keep the storefront's own wording.
  protected readonly topPicksHeading = signal('');
  // Stores ranked by their deepest markdown. Empty means the section is not rendered at all: it
  // used to fall back to a hardcoded logo row (Amazon, Alibaba, Rakuten…) so it "never rendered
  // empty", but that row linked mostly to /storelist and advertised discounts nobody was running.
  // A heading that promises "the biggest discounts right now" over placeholder logos is worse than
  // no section.
  protected readonly topDiscountStores = signal<EventTopStore[]>([]);
  // Stores attached to the featured event that have products. The section hides when the event has
  // none — better an absent row than one padded with stores that have nothing to show.
  protected readonly eventStoreCards = signal<DealBrandCard[]>([]);
  protected readonly featuredEventName = signal('');
  // Admin-featured store heroes shown after Top Stores. Empty = section hidden; one = single hero;
  // many = carousel.
  protected readonly featuredHeroes = signal<FeaturedHero[]>([]);
  protected readonly currentHeroSlide = signal(0);
  protected readonly hasHero = computed(() => this.heroSlides().length > 0);
  protected readonly hasHeroCarousel = computed(() => this.heroSlides().length > 1);

  // ── Event top stores (static logos for now) ───────────────────────────────────
  protected readonly topStoresHeading = 'Top Stores';

  // ── Top Picks for you (static brand directory) ────────────────────────────────

  // -- Blog posts -- the latest published guides, continuing where the home page stops ---------
  // Populated by loadBlogPosts(); empty until it resolves, so the section renders no cards.
  protected readonly blogPosts = signal<DealBlogPost[]>([]);

  // ── Trust marquee ─────────────────────────────────────────────────────────────
  protected readonly trustItems: string[] = [
    'Verified Coupons',
    '100% Free to Use',
    'Updated Every Hour',
    'Trusted by Millions',
    'Exclusive Codes',
    'Cashback Deals',
  ];

  // ── Popular Categories ────────────────────────────────────────────────────────
  /**
   * Tab shells only — the categories inside come from the API, exactly as they do on the home page.
   *
   * These two tabs used to hold sixteen hardcoded labels ("Outerwear", "Streetwear", "Bags"…) with
   * stock thumbnails and no links, none of which matched the categories the site actually runs. The
   * dev flag on this section said as much.
   */
  private readonly defaultPopularCategoryTabs: PopularCategoryTab[] = [
    { id: 'coupons-offers', label: 'Coupons & Offers', categories: [] },
    { id: 'products-deals', label: 'Products & Deals', categories: [] },
  ];

  protected readonly categoryProducts = signal<CategoryProductsSection[]>([]);

  private readonly popularCouponCategories = signal<PopularCategoryItem[]>([]);
  private readonly popularDealCategories = signal<PopularCategoryItem[]>([]);

  protected readonly popularCategoryTabs = computed<PopularCategoryTab[]>(() => {
    const coupons = this.popularCouponCategories();
    const deals = this.popularDealCategories();

    return (
      this.defaultPopularCategoryTabs
        .map((tab) => ({
          ...tab,
          categories: tab.id === 'coupons-offers' ? coupons : deals,
        }))
        // A tab with nothing behind it is worse than no tab.
        .filter((tab) => tab.categories.length > 0)
    );
  });
  protected readonly popularCardsPerSlide = 5;
  protected readonly selectedPopularCategoryTabId = signal(this.defaultPopularCategoryTabs[0].id);
  protected readonly currentPopularCategorySlide = signal(0);

  protected readonly selectedPopularCategoryTab = computed<PopularCategoryTab>(() => {
    const tabs = this.popularCategoryTabs();
    // Tabs drop out when their API call comes back empty, so the selected id can point at a tab that
    // is no longer there — fall back to the first one that survived.
    return (
      tabs.find((tab) => tab.id === this.selectedPopularCategoryTabId()) ??
      tabs[0] ??
      this.defaultPopularCategoryTabs[0]
    );
  });

  protected readonly popularCategoryTotalSlides = computed(() =>
    Math.max(
      1,
      this.selectedPopularCategoryTab().categories.length - this.popularCardsPerSlide + 1,
    ),
  );

  protected readonly popularCategorySlideIndexes = computed(() =>
    Array.from({ length: this.popularCategoryTotalSlides() }, (_, index) => index),
  );

  protected readonly visiblePopularCategories = computed(() => {
    const start = this.currentPopularCategorySlide();
    return this.selectedPopularCategoryTab().categories.slice(
      start,
      start + this.popularCardsPerSlide,
    );
  });

  constructor() {
    this.applyDealsSeo();
    this.loadHeroSlides();
    this.loadNewArrivals();
    this.loadTopPicks();
    this.loadTopDiscountStores();
    this.loadFeaturedEventStores();
    this.loadFeaturedHeroes();
    this.loadPopularCategories();
    this.loadCategoryProducts();
    this.loadBlogPosts();
    this.startHeroAutoplay();
    this.startPopularCategoryAutoplay();
  }

  /**
   * Same two feeds the home page's Popular Categories runs on, so both pages show the same live
   * categories: coupon categories in the first tab, deal categories in the second.
   */
  private loadPopularCategories(): void {
    this.api
      .getPopularCategories()
      .pipe(
        catchError(() => of({ success: false, data: [] as FrontendPopularCategory[] })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        const items = (response.data ?? [])
          .map((category) => this.toPopularCategoryItem(category))
          .filter((item) => item.label.length > 0);
        this.popularCouponCategories.set(items);
      });

    this.api
      .getDealsCategories(12)
      .pipe(
        catchError(() => of({ success: false, data: [] as FrontendDealCategoryListItem[] })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        const items = (response.data ?? [])
          .map((category) => this.toPopularDealCategoryItem(category))
          .filter((item) => item.label.length > 0 && Boolean(item.path));
        this.popularDealCategories.set(items);
      });
  }

  private toPopularCategoryItem(category: FrontendPopularCategory): PopularCategoryItem {
    const iconType = (category.iconType || '').trim();
    const imagePath = (category.iconImagePath || '').trim();
    const iconCode = (category.iconCode || '').trim();
    const slug = (category.slug || '').trim();

    return {
      label: (category.name || '').trim(),
      slug,
      path: slug ? `/categories/${slug}` : '',
      imageUrl: iconType === 'image' && imagePath ? this.assetUrl(imagePath) : '',
      iconHtml:
        iconType === 'code' && iconCode ? this.sanitizer.bypassSecurityTrustHtml(iconCode) : null,
    };
  }

  /**
   * The admin-chosen category row (Settings » Deals Page » Category Products).
   *
   * The whole section hides when no category is set, so nothing here has a static fallback: an
   * unconfigured setting should leave no trace on the page.
   */
  private loadCategoryProducts(): void {
    const transferred = this.transferState.get(DEALS_CATEGORY_PRODUCTS_KEY, null);
    if (transferred && transferred.length > 0) {
      this.categoryProducts.set(transferred);
      return;
    }

    this.api
      .getDealsCategoryProducts()
      .pipe(
        map((response) => this.toCategoryProductsSections(response.data)),
        catchError(() => of<CategoryProductsSection[]>([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sections) => {
        this.categoryProducts.set(sections);
        if (this.isServer) {
          this.transferState.set(DEALS_CATEGORY_PRODUCTS_KEY, sections);
        }
      });
  }

  /**
   * The latest published guides, continuing where the home page's "Insights & Perspectives" row
   * stops: it shows the newest `LATEST_INSIGHTS_DISPLAY_LIMIT` posts, so this one skips exactly that
   * many and takes the next `BLOG_DISPLAY_LIMIT`. The two rows never repeat an article, and moving
   * the home page's count moves this offset with it.
   *
   * Ordering matches that section's rule — `publishAt` when the admin set one, otherwise
   * `updatedAt` — so "the next six" really are the next six and not a differently-sorted slice.
   */
  private loadBlogPosts(): void {
    const transferred = this.transferState.get(DEALS_BLOG_POSTS_KEY, null);
    if (transferred && transferred.length > 0) {
      this.blogPosts.set(transferred);
      return;
    }

    this.api
      .getBlogs({ page: 1, limit: BLOG_FETCH_LIMIT, fields: 'card' })
      .pipe(
        map((response) => this.toDealBlogPosts(response.data ?? [])),
        catchError(() => of<DealBlogPost[]>([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((posts) => {
        this.blogPosts.set(posts);
        if (this.isServer) {
          this.transferState.set(DEALS_BLOG_POSTS_KEY, posts);
        }
      });
  }

  private toDealBlogPosts(blogs: FrontendBlog[]): DealBlogPost[] {
    return blogs
      .map((blog) => ({ blog, timestamp: this.blogTimestamp(blog) }))
      .sort((a, b) => b.timestamp - a.timestamp)
      .map(({ blog }) => this.toDealBlogPost(blog))
      .filter((post): post is DealBlogPost => post !== null)
      .slice(BLOG_SKIP, BLOG_SKIP + BLOG_DISPLAY_LIMIT);
  }

  private toDealBlogPost(blog: FrontendBlog): DealBlogPost | null {
    const title = this.stripHtml(blog.name ?? '');
    const slug = (blog.slug ?? '').trim();
    // Both are required: the card is one big link, and a card with no title is an empty box.
    if (!title || !slug) return null;

    const store = Array.isArray(blog.stores) ? blog.stores[0] : undefined;
    const storeLogo = typeof store?.logoImagePath === 'string' ? store.logoImagePath.trim() : '';
    const author = this.stripHtml(typeof blog.author === 'string' ? blog.author : '');

    return {
      slug,
      title,
      excerpt: this.blogExcerpt(blog),
      category: this.stripHtml(blog.category?.name ?? '') || 'Shopping Tips',
      readTime: `${this.blogReadMinutes(blog)} min read`,
      // The list endpoint populates the creator with a name only — no slug to link to — so the card
      // shows the team name as plain text rather than a link that goes nowhere.
      author: author || 'SavingArena Team',
      authorPath: '',
      imageUrl: blog.thumbnailImagePath
        ? this.assetUrl(blog.thumbnailImagePath)
        : '/images/blog-placeholder.svg',
      storeLogoUrl: storeLogo ? this.assetUrl(storeLogo) : '',
    };
  }

  private blogExcerpt(blog: FrontendBlog): string {
    const shortDescription = this.stripHtml(blog.shortDescription ?? '');
    if (shortDescription) return shortDescription;

    const content = this.stripHtml(blogBodyText(blog));
    if (!content) return this.stripHtml(blog.name ?? '');

    return content.length <= 150 ? content : `${content.slice(0, 150).trimEnd()}...`;
  }

  private blogReadMinutes(blog: FrontendBlog): number {
    return blogReadMinutes(blog);
  }

  private blogTimestamp(blog: FrontendBlog): number {
    const value = blog.publishAt || blog.updatedAt;
    if (!value) return 0;
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }

  private stripHtml(value: string): string {
    return String(value ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private toCategoryProductsSections(
    data: FrontendDealsCategoryProducts | null | undefined,
  ): CategoryProductsSection[] {
    return (data?.sections ?? [])
      .map((section) => ({
        categoryName: section.category.name,
        categoryPath: section.category.path,
        products: (section.products ?? [])
          .map((product) => this.toNewArrival(product))
          // A card with no image or title would break the row's fixed-height slots.
          .filter((product) => product.image.length > 0 && product.title.length > 0),
      }))
      .filter((section) => section.products.length > 0 && section.categoryName.length > 0);
  }

  private toPopularDealCategoryItem(category: FrontendDealCategoryListItem): PopularCategoryItem {
    const slug = (category.slug || '').trim();
    // The admin's category icon when there is one, else the category's newest product image — so a
    // tile is never an empty circle.
    const imagePath =
      (category.iconImagePath || '').trim() || (category.productImagePath || '').trim();

    return {
      label: (category.name || '').trim(),
      slug,
      path: slug ? `/collection/${slug}` : '',
      imageUrl: imagePath ? this.assetUrl(imagePath) : '',
      iconHtml: null,
    };
  }

  // Only the hero is admin-managed; the rest of this page is static design content.
  private loadHeroSlides(): void {
    // Seed synchronously from what the server already fetched so the client's first paint matches
    // the server-rendered hero exactly. The key is deliberately NOT removed: consuming it would make
    // the data single-use, and any second construction of this component (hydration re-create, or
    // navigating back to /deals) would then start empty, drop the `@if`, and make the banner vanish
    // until a fresh request resolved — the "shows then hides" flash.
    // An empty transferred array is still truthy, so check the length: if the server came back with
    // nothing (e.g. the backend was briefly down during SSR) we fall through and retry on the client
    // rather than hiding the hero for the whole session.
    const transferred = this.transferState.get(DEALS_HERO_KEY, null);
    if (transferred && transferred.length > 0) {
      this.heroSlides.set(transferred);
      return;
    }

    this.api
      .getDeals()
      .pipe(
        map((response) =>
          (response.data?.banner?.slides ?? []).map((slide) => this.toHeroSlide(slide)),
        ),
        catchError(() => of<HeroSlide[]>([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((slides) => {
        const usable = slides.filter((slide) => slide.imageUrl.length > 0);
        this.heroSlides.set(usable);
        this.currentHeroSlide.set(0);
        if (this.isServer) {
          this.transferState.set(DEALS_HERO_KEY, usable);
        }
      });
  }

  private loadNewArrivals(): void {
    const transferred = this.transferState.get(DEALS_NEW_ARRIVALS_KEY, null);
    if (transferred && transferred.length > 0) {
      this.newArrivals.set(transferred);
      return;
    }

    this.api
      .getLatestProductsPerStore(NEW_ARRIVALS_LIMIT)
      .pipe(
        map((response) => (response.data ?? []).map((product) => this.toNewArrival(product))),
        catchError(() => of<DealProduct[]>([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((products) => {
        // A card with no image or title would break the row's fixed-height slots.
        const usable = products.filter(
          (product) => product.image.length > 0 && product.title.length > 0,
        );
        this.newArrivals.set(usable);
        if (this.isServer) {
          this.transferState.set(DEALS_NEW_ARRIVALS_KEY, usable);
        }
      });
  }

  /**
   * "Shop Our Top Picks". The API picks the source: the admin's chosen stores when Settings » Deals
   * Page » Top Picks has any, otherwise the automatic biggest-discount ranking. Nothing here has to
   * know which — it renders whatever comes back, and an admin heading when one is set.
   */
  private loadTopPicks(): void {
    const transferred = this.transferState.get(DEALS_TOP_PICKS_KEY, null);
    if (transferred && transferred.products.length > 0) {
      this.topPicks.set(transferred.products);
      this.topPicksHeading.set(transferred.sectionTitle);
      return;
    }

    this.api
      .getDealsTopPicks(TOP_PICKS_LIMIT)
      .pipe(
        map((response) => ({
          sectionTitle: (response.data?.sectionTitle ?? '').trim(),
          // A card with no image or title would break the row's fixed-height slots.
          products: (response.data?.products ?? [])
            .map((product) => this.toNewArrival(product))
            .filter((product) => product.image.length > 0 && product.title.length > 0),
        })),
        catchError(() => of({ sectionTitle: '', products: [] as DealProduct[] })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((row) => {
        this.topPicks.set(row.products);
        this.topPicksHeading.set(row.sectionTitle);
        if (this.isServer) {
          this.transferState.set(DEALS_TOP_PICKS_KEY, row);
        }
      });
  }

  private loadTopDiscountStores(): void {
    const transferred = this.transferState.get(DEALS_TOP_STORES_KEY, null);
    if (transferred && transferred.length > 0) {
      this.topDiscountStores.set(transferred);
      return;
    }

    this.api
      .getTopDiscountStores(TOP_STORES_LIMIT)
      .pipe(
        map((response) =>
          (response.data ?? [])
            .filter((store) => store.logoImagePath && store.slug)
            .map((store) => ({
              name: store.name,
              logoUrl: this.assetUrl(store.logoImagePath),
              path: `/${store.slug}`,
            })),
        ),
        catchError(() => of<EventTopStore[]>([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((stores) => {
        this.topDiscountStores.set(stores);
        if (this.isServer) {
          this.transferState.set(DEALS_TOP_STORES_KEY, stores);
        }
      });
  }

  private loadFeaturedEventStores(): void {
    const transferred = this.transferState.get(DEALS_EVENT_STORES_KEY, null);
    if (transferred && transferred.length > 0) {
      this.eventStoreCards.set(transferred);
      return;
    }

    this.api
      .getFeaturedEventStores(EVENT_STORES_LIMIT)
      .pipe(
        map((response) => ({
          eventName: response.data?.event?.name ?? '',
          cards: (response.data?.stores ?? []).map((store) => this.toEventStoreCard(store)),
        })),
        catchError(() => of({ eventName: '', cards: [] as DealBrandCard[] })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ eventName, cards }) => {
        this.featuredEventName.set(eventName);
        this.eventStoreCards.set(cards);
        if (this.isServer) {
          this.transferState.set(DEALS_EVENT_STORES_KEY, cards);
        }
      });
  }

  private toEventStoreCard(store: FrontendFeaturedEventStore): DealBrandCard {
    return {
      name: store.name,
      logoUrl: this.assetUrl(store.logoImagePath ?? ''),
      couponsCount: store.couponsCount ?? 0,
      href: store.slug ? `/${store.slug}` : '/storelist',
    };
  }

  private toNewArrival(product: FrontendProduct): DealProduct {
    const store = product.store;

    return {
      title: (product.title || product.name || '').trim(),
      price: (product.newPrice ?? '').trim(),
      oldPrice: (product.oldPrice ?? '').trim(),
      // Left empty so the card derives "SAVE <currency> x" from the two prices itself.
      save: '',
      reviews: 0,
      image: this.assetUrl(product.thumbnailImagePath ?? ''),
      hoverImage: this.toHoverImage(product),
      storeName: (store?.name ?? '').trim(),
      storeLogoUrl: this.assetUrl(store?.logoImagePath ?? ''),
      // Slug when the product has one (SEO-readable URL); id only as a fallback.
      href: `/${(product.slug ?? '').trim() || product.id}`,
    };
  }

  // First gallery angle that genuinely differs from the thumbnail; '' when the feed gave only one.
  private toHoverImage(product: FrontendProduct): string {
    const mainImagePath = product.thumbnailImagePath ?? '';
    const alternate = (product.galleryImagePaths ?? []).find(
      (path) => path && path !== mainImagePath,
    );

    return alternate ? this.assetUrl(alternate) : '';
  }

  private toHeroSlide(slide: FrontendDealsBannerSlide): HeroSlide {
    const link = (slide.linkUrl ?? '').trim();
    return {
      imageUrl: this.assetUrl(slide.imagePath ?? ''),
      altText: (slide.altText ?? '').trim() || 'SavingArena deals',
      linkUrl: link || '/collection',
      isExternal: /^https?:\/\//i.test(link),
    };
  }

  private assetUrl(path: string): string {
    const trimmed = path.trim();
    if (!trimmed || /^https?:\/\//i.test(trimmed)) return trimmed;
    const base = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    return `${base}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`;
  }

  // The rest of the page's copy is static (no route params), so the constructor is the right place.
  private applyDealsSeo(): void {
    const url = this.seo.absolute(DEALS_PATH);

    this.seo.apply({
      title: DEALS_TITLE,
      description: DEALS_DESCRIPTION,
      canonicalPath: DEALS_PATH,
    });

    this.seo.setJsonLd([
      webPageSchema({
        origin: this.seo.origin,
        url,
        name: DEALS_TITLE,
        description: DEALS_DESCRIPTION,
      }),
      breadcrumbSchemaFor(this.crumbs, (p) => this.seo.absolute(p)),
    ]);
  }

  ngOnDestroy(): void {
    this.stopHeroAutoplay();
    this.stopPopularCategoryAutoplay();
  }

  // ── Hero carousel ─────────────────────────────────────────────────────────────
  protected selectHeroSlide(index: number): void {
    this.currentHeroSlide.set(index);
    this.restartHeroAutoplay();
  }

  protected showPreviousHeroSlide(): void {
    this.currentHeroSlide.update(
      (i) => (i - 1 + this.heroSlides().length) % this.heroSlides().length,
    );
    this.restartHeroAutoplay();
  }

  protected showNextHeroSlide(): void {
    this.currentHeroSlide.update((i) => (i + 1) % this.heroSlides().length);
    this.restartHeroAutoplay();
  }

  private startHeroAutoplay(): void {
    if (this.heroAutoplayTimer !== null) return;
    this.heroAutoplayTimer = setInterval(() => {
      const total = this.heroSlides().length;
      if (total > 1) this.currentHeroSlide.update((i) => (i + 1) % total);
    }, 5000);
  }

  private stopHeroAutoplay(): void {
    if (this.heroAutoplayTimer === null) return;
    clearInterval(this.heroAutoplayTimer);
    this.heroAutoplayTimer = null;
  }

  private restartHeroAutoplay(): void {
    this.stopHeroAutoplay();
    this.startHeroAutoplay();
  }

  // Mirrors the blog page helper so card titles clamp at the same length.
  protected truncateText(value: string, max: number): string {
    const text = value.trim();
    return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
  }

  // ── Event top stores carousel ─────────────────────────────────────────────────
  // Scroll-based rather than paged: the tile count that fits changes per breakpoint, so nudging
  // the track by ~80% of its visible width works at every size without measuring the viewport.
  private loadFeaturedHeroes(): void {
    this.api
      .getDealsFeaturedHeroes()
      .pipe(
        map((response) =>
          (response.data ?? [])
            .filter((hero) => hero.storeKey)
            .map((hero) => ({
              name: hero.name,
              slug: hero.slug,
              path: hero.categorySlug
                ? `/collection/${hero.categorySlug}/${hero.storeKey}`
                : `/deals/${hero.storeKey}`,
              logoUrl: this.assetUrl(hero.logoImagePath),
              bannerUrl: this.assetUrl(hero.bannerImagePath),
              affiliateLink: hero.affiliateLink,
              buttonText: hero.buttonText,
              followers: Number(hero.followers || 0),
              shares: Number(hero.shares || 0),
              shareLabel: 'Share',
            })),
        ),
        catchError(() => of<FeaturedHero[]>([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((heroes) => this.featuredHeroes.set(heroes));
  }

  /** 24600 -> "24.6K". Keeps the badges narrow enough to sit on the banner at any width. */
  protected formatCount(value: number): string {
    return formatCompactCount(value);
  }

  /**
   * Share a featured store: hand the page to the OS share sheet where there is one (phones, and
   * Safari), otherwise copy the link. Either way the share is counted server-side and the badge
   * updates from the count the API returns, so it never drifts from what is stored.
   *
   * The counter moves only when the visitor actually completed the share — cancelling the sheet
   * throws `AbortError`, which must not count.
   */
  protected async shareHero(hero: FeaturedHero): Promise<void> {
    if (!hero.slug || this.isServer) return;

    const url = `${window.location.origin}/${hero.slug}`;
    let label = 'Shared';

    try {
      if (navigator.share) {
        await navigator.share({ title: hero.name, url });
      } else {
        await navigator.clipboard.writeText(url);
        label = 'Link copied';
      }
    } catch (error) {
      // Visitor dismissed the share sheet — not a share, and not an error worth showing.
      if ((error as DOMException)?.name === 'AbortError') return;
      label = 'Copy failed';
      this.setHeroShareLabel(hero, label);
      return;
    }

    this.setHeroShareLabel(hero, label);
    this.api
      .shareStore(hero.slug)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        const shares = response?.data?.shares;
        if (typeof shares !== 'number') return;
        this.featuredHeroes.update((heroes) =>
          heroes.map((item) => (item.slug === hero.slug ? { ...item, shares } : item)),
        );
      });
  }

  private setHeroShareLabel(hero: FeaturedHero, label: string): void {
    this.featuredHeroes.update((heroes) =>
      heroes.map((item) => (item.slug === hero.slug ? { ...item, shareLabel: label } : item)),
    );
    setTimeout(() => {
      this.featuredHeroes.update((heroes) =>
        heroes.map((item) => (item.slug === hero.slug ? { ...item, shareLabel: 'Share' } : item)),
      );
    }, 2000);
  }

  protected scrollFeaturedHeroes(direction: -1 | 1): void {
    const track = this.featuredHeroTrack?.nativeElement;
    if (track) {
      track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
    }
  }

  protected scrollTopStores(direction: -1 | 1): void {
    const track = this.topStoresTrack?.nativeElement;
    if (!track) return;
    track.scrollBy({
      left: direction * Math.max(240, track.clientWidth * 0.8),
      behavior: 'smooth',
    });
  }

  // ── Popular Categories ────────────────────────────────────────────────────────
  protected selectPopularCategoryTab(tabId: string): void {
    this.selectedPopularCategoryTabId.set(tabId);
    this.currentPopularCategorySlide.set(0);
    this.restartPopularCategoryAutoplay();
  }

  protected selectPopularCategorySlide(index: number): void {
    this.currentPopularCategorySlide.set(index);
    this.restartPopularCategoryAutoplay();
  }

  private startPopularCategoryAutoplay(): void {
    if (this.popularCategoryAutoplayTimer !== null || this.popularCategoryTotalSlides() < 2) return;
    this.popularCategoryAutoplayTimer = setInterval(() => {
      const totalSlides = this.popularCategoryTotalSlides();
      this.currentPopularCategorySlide.update((index) => (index + 1) % totalSlides);
    }, 5000);
  }

  private stopPopularCategoryAutoplay(): void {
    if (this.popularCategoryAutoplayTimer === null) return;
    clearInterval(this.popularCategoryAutoplayTimer);
    this.popularCategoryAutoplayTimer = null;
  }

  private restartPopularCategoryAutoplay(): void {
    this.stopPopularCategoryAutoplay();
    this.startPopularCategoryAutoplay();
  }
}
